import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  InputAdornment,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { TaskAssignmentDialog, TaskDetailsDialog } from './TaskDialogs';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FlagIcon from '@mui/icons-material/Flag';
import { DensityMedium, PersonAdd } from '@mui/icons-material';

// ------------------ CONFIG MAPS ------------------
const priorityConfig = {
  1: {
    label: 'Critical',
    icon: <DragHandleIcon sx={{ fontSize: 14 }} />,
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#93C5FD',
  },
  2: {
    label: 'High',
    icon: <PriorityHighIcon sx={{ fontSize: 14 }} />,
    color: '#B91C1C',
    bg: '#FEF2F2',
    border: '#FCA5A5',
  },
  3: {
    label: 'Medium',
    icon: <DensityMedium sx={{ fontSize: 14 }} />,
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FCD34D',
  },
  4: {
    label: 'Low',
    icon: <LowPriorityIcon sx={{ fontSize: 14 }} />,
    color: '#15803D',
    bg: '#F0FDF4',
    border: '#86EFAC',
  },
};

const assignmentStatusConfig = {
  Pending: {
    icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
    color: '#C2410C',
    bg: '#FFF7ED',
    border: '#FDBA74',
  },
  Assigned: {
    icon: <AssignmentIndIcon sx={{ fontSize: 14 }} />,
    color: '#6D28D9',
    bg: '#F5F3FF',
    border: '#C4B5FD',
  },
  'In Progress': {
    icon: <AutorenewIcon sx={{ fontSize: 14 }} />,
    color: '#1D4ED8',
    bg: '#EFF6FF',
    border: '#93C5FD',
  },
  'On Hold': {
    icon: <AccessTimeIcon sx={{ fontSize: 14 }} />,
    color: '#92400E',
    bg: '#FFFBEB',
    border: '#FCD34D',
  },
  Rejected: {
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    color: '#B91C1C',
    bg: '#FEF2F2',
    border: '#FCA5A5',
  },
  Completed: {
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    color: '#15803D',
    bg: '#F0FDF4',
    border: '#86EFAC',
  },
  Cancelled: {
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    color: '#991B1B',
    bg: '#FEF2F2',
    border: '#F87171',
  },
};

// Avatar gradients (cycles through)
const avatarGradients = [
  'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
  'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
  'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
  'linear-gradient(135deg, #f59e0b 0%, #fb923c 100%)',
];

const getInitials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const DUE_SOON_WINDOW_DAYS = 3;

// Traffic-light urgency for a pending task's deadline: red once it's overdue,
// amber inside the due-soon window, green otherwise. A completed task's
// deadline no longer carries urgency, so it's shown neutral/gray.
const getDeadlineUrgency = (dateStr, assignmentStatus) => {
  if (!dateStr) return { label: 'No due date', color: '#94A3B8', bg: '#F1F5F9', border: '#E2E8F0' };
  if (assignmentStatus === 5) return { label: 'Completed', color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0' };

  const daysLeft = Math.ceil((new Date(dateStr).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);

  if (daysLeft < 0) return { label: 'Overdue', color: '#EF4444', bg: '#FEF2F2', border: '#FECACA' };
  if (daysLeft <= DUE_SOON_WINDOW_DAYS) return { label: 'Due soon', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' };
  return { label: 'On track', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' };
};

// ------------------ COMPONENT ------------------
const PAGE_SIZE = 10;

const TasksTableFromDrag = ({ tasks = [], filters }) => {
  const data = tasks || [];

  const [assignFilter, setAssignFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const filteredData = data.filter((task) => {
    if (assignFilter === 'all') return true;
    const assigned = task?.task_assigned_to !== null && task?.task_assigned_to !== undefined && task?.task_assigned_to !== '';
    if (assignFilter === 'assigned') return assigned;
    if (assignFilter === 'unassigned') return !assigned;
    return true;
  });

  // Free-text search across title, people, department and task id
  const query = searchQuery.trim().toLowerCase();
  const searchedData = query
    ? filteredData.filter((t) =>
        [t.title, t.assigned_to_name, t.assigned_by_name, t.target_dept_name, `#${t.task_id}`, t.task_id]
          .filter((v) => v !== null && v !== undefined)
          .some((v) => String(v).toLowerCase().includes(query)),
      )
    : filteredData;

  // Front-end pagination (10 per page)
  const pageCount = Math.max(1, Math.ceil(searchedData.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paginatedData = searchedData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset to first page whenever the result set changes
  useEffect(() => {
    setPage(1);
  }, [assignFilter, searchQuery, data.length]);

  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    task: null,
  });
  const [assignDialog, setAssignDialog] = useState({ open: false, task: null });

  const handleView = (task) => {
    setDetailsDialog({
      open: true,
      task: { raw: task },
    });
  };

  const handleOpenAssign = (task) => {
    // DragAndDrop's TaskAssignmentDialog expects task.raw -> wrap raw task for compatibility
    setAssignDialog({ open: true, task: { raw: task } });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: '#FFFFFF' }}>
      {/* Section Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
            }}
          >
            <ListAltIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1E293B',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}
            >
              My Tasks
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.78rem' }}>
              {searchedData?.length || 0} task{searchedData?.length !== 1 ? 's' : ''} shown
            </Typography>
          </Box>
        </Stack>

        {/* Search + Filter */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Filter: Assigned / Unassigned */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Assignment</InputLabel>
            <Select
              value={assignFilter}
              label="Assignment"
              onChange={(e) => setAssignFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
            </Select>
          </FormControl>
        </Stack>
          </Stack>

      {/* Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          overflow: 'auto',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <Table sx={{ minWidth: 1100 }}>
          {/* Header with DragAndDrop gradient */}
          <TableHead>
            <TableRow
              sx={{
                background: 'linear-gradient(135deg, #63f197 0%, #2fab50 100%)',
                '& th': {
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  borderBottom: 'none',
                  py: 2,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <TableCell sx={{ pl: 3, width: 60 }}>#</TableCell>
              <TableCell sx={{ minWidth: 280 }}>Task Title</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Target Department</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Assigned To</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Assigned By</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Priority</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Due Date</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Created On</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Status</TableCell>
              <TableCell sx={{ pr: 3, width: 80, textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!searchedData || searchedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ py: 8, textAlign: 'center' }}>
                  <ListAltIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 600 }}>
                    No tasks available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
            paginatedData.map((task, index) => {
                const priority = priorityConfig[task.priority] || priorityConfig[1];
                const status = assignmentStatusConfig[task.assignment_status_name] || assignmentStatusConfig['Pending'];
                const urgency = getDeadlineUrgency(task.expected_delivery_date, task.assignment_status);
                const isOverdue = urgency.label === 'Overdue';

                const assigneeGradient = avatarGradients[index % avatarGradients.length];

                return (
                  <TableRow
                    key={task.task_id}
                    sx={{
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: '#F8FAFC',
                      },
                      '& td': {
                        borderBottom: '1px solid #F1F5F9',
                        py: 2,
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableCell sx={{ pl: 3 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          color: '#6366F1',
                        }}
                      >
                        {(currentPage - 1) * PAGE_SIZE + index + 1}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: '#1E293B',
                            fontSize: '0.86rem',
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {task.title}
                        </Typography>
                        {isOverdue && (
                          <Tooltip title="Overdue" arrow>
                            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#EF4444' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.3 }}>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.7rem' }}>
                          ID: #{task.task_id}
                        </Typography>
                        {task.pms_link_origin === 'job_to_task' && (
                          <Tooltip title="Auto-created from a PMS goal job" arrow>
                            <Stack direction="row" alignItems="center" spacing={0.4}>
                              <FlagIcon sx={{ fontSize: 13, color: '#7E57C2' }} />
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#7E57C2' }}
                              >
                                From PMS
                              </Typography>
                            </Stack>
                          </Tooltip>
                        )}
                        {Number(task.total_subtasks) > 0 && (
                          <Stack direction="row" alignItems="center" spacing={0.4}>
                            <ChecklistIcon
                              sx={{
                                fontSize: 13,
                                color:
                                  Number(task.completed_subtasks) === Number(task.total_subtasks) ? '#16A34A' : '#94A3B8',
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color:
                                  Number(task.completed_subtasks) === Number(task.total_subtasks) ? '#16A34A' : '#64748B',
                              }}
                            >
                              {task.completed_subtasks}/{task.total_subtasks} subtasks
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                      {Array.isArray(task.tags) && task.tags.length > 0 && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.4, flexWrap: 'wrap' }}>
                          {task.tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.62rem',
                                fontWeight: 600,
                                background: `${tag.color}22`,
                                color: tag.color,
                                border: `1px solid ${tag.color}55`,
                                borderRadius: '4px',
                                '& .MuiChip-label': { px: 0.6 },
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <BusinessIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.83rem', color: '#334155', fontWeight: 500 }}>{task.target_dept_name || '—'}</Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {task.assigned_to_name ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 30, height: 30, background: assigneeGradient, fontSize: '0.72rem', fontWeight: 700, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>{getInitials(task.assigned_to_name || '—')}</Avatar>
                          <Typography sx={{ fontSize: '0.83rem', color: '#334155', fontWeight: 500 }}>{task.assigned_to_name}</Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonOutlineIcon sx={{ fontSize: 18, color: '#CBD5E1' }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontStyle: 'italic' }}>Unassigned</Typography>
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      {task.created_by_name ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 30, height: 30, background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)', fontSize: '0.72rem', fontWeight: 700, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>{getInitials(task.assigned_by_name || '—')}</Avatar>
                          <Typography sx={{ fontSize: '0.83rem', color: '#334155', fontWeight: 500 }}>{task.assigned_by_name}</Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonOutlineIcon sx={{ fontSize: 18, color: '#CBD5E1' }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', fontStyle: 'italic' }}>Unassigned</Typography>
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={React.cloneElement(priority.icon, { sx: { fontSize: 14, color: `${priority.color} !important` } })}
                        label={priority.label}
                        size="small"
                        sx={{
                          height: 26,
                          background: priority.bg,
                          color: priority.color,
                          border: `1px solid ${priority.border}`,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          borderRadius: '6px',
                          '& .MuiChip-icon': { color: `${priority.color} !important`, ml: '6px' },
                          '& .MuiChip-label': { px: 0.9 },
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={<CalendarTodayIcon sx={{ fontSize: '13px !important' }} />}
                        label={`${formatDate(task.expected_delivery_date)} · ${urgency.label}`}
                        size="small"
                        sx={{
                          height: 24,
                          background: urgency.bg,
                          color: urgency.color,
                          border: `1px solid ${urgency.border}`,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          borderRadius: '6px',
                          '& .MuiChip-icon': { color: `${urgency.color} !important`, ml: '6px' },
                          '& .MuiChip-label': { px: 0.8 },
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: '#64748B' }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#334155', fontWeight: 500 }}>{formatDate(task.created_at)}</Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={React.cloneElement(status.icon, { sx: { fontSize: 14, color: `${status.color} !important` } })}
                        label={task.assignment_status_name || 'Pending'}
                        size="small"
                        sx={{
                          height: 26,
                          background: status.bg,
                          color: status.color,
                          border: `1px solid ${status.border}`,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          borderRadius: '6px',
                          '& .MuiChip-icon': { color: `${status.color} !important`, ml: '6px' },
                          '& .MuiChip-label': { px: 0.9 },
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ pr: 3, textAlign: 'center' }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details" arrow>
                          <IconButton size="small" onClick={() => handleView(task)} sx={{ width: 34, height: 34, background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)', color: '#6366F1', transition: 'all 0.2s ease', '&:hover': { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#FFFFFF', transform: 'scale(1.05)' } }}>
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Assign" arrow>
                          <Button startIcon={<PersonAdd sx={{ fontSize: 18 }} />} size="small" onClick={() => handleOpenAssign(task)} color='secondary' variant='outlined'>
                          Assign
                          </Button>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {searchedData.length > PAGE_SIZE && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.78rem' }}>
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, searchedData.length)} of{' '}
            {searchedData.length}
          </Typography>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={(e, value) => setPage(value)}
            color="primary"
            shape="rounded"
            size="small"
          />
        </Stack>
      )}

      {detailsDialog.open && detailsDialog.task && (
        <TaskDetailsDialog
          open={detailsDialog.open}
          onClose={() => setDetailsDialog({ open: false, task: null })}
          task={detailsDialog.task}
          filters={filters}
        />
      )}

      {assignDialog.open && assignDialog.task && (
        <TaskAssignmentDialog
          open={assignDialog.open}
          onClose={() => setAssignDialog({ open: false, task: null })}
          task={assignDialog.task}
          filters={filters}
        />
      )}
    </Box>
  );
};

export default TasksTableFromDrag;
